import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Loader2, MessageSquare, Phone, Video, Clock, Calendar,
  ArrowLeft, ExternalLink
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-purple-100 text-purple-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
  NO_SHOW: 'bg-gray-100 text-gray-800'
};

const TYPE_ICONS = {
  TEXT: MessageSquare,
  AUDIO: Phone,
  VIDEO: Video
};

export default function MyConsultationsPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [consultations, setConsultations] = useState([]);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadConsultations();
  }, [token]);

  const loadConsultations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/my-consultations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsultations(response.data || []);
    } catch (error) {
      console.error('Error loading consultations:', error);
      toast.error('Failed to load consultations');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (bookingId) => {
    try {
      const response = await axios.get(`${API_URL}/api/consultation-bookings/${bookingId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const selectConsultation = (consultation) => {
    setSelectedConsultation(consultation);
    loadMessages(consultation.id);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConsultation) return;
    setSendingMessage(true);
    try {
      await axios.post(
        `${API_URL}/api/consultation-bookings/${selectedConsultation.id}/messages`,
        { message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      loadMessages(selectedConsultation.id);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const filterConsultations = (status) => {
    if (status === 'upcoming') {
      return consultations.filter(c => ['PENDING', 'CONFIRMED'].includes(c.status));
    } else if (status === 'active') {
      return consultations.filter(c => c.status === 'IN_PROGRESS');
    } else {
      return consultations.filter(c => ['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(c.status));
    }
  };

  const formatPrice = (cents) => {
    if (!cents) return 'Free';
    return `$${(cents / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const ConsultationCard = ({ consultation }) => {
    const TypeIcon = TYPE_ICONS[consultation.consultationType] || MessageSquare;
    return (
      <Card 
        className={`cursor-pointer transition-colors hover:border-primary ${
          selectedConsultation?.id === consultation.id ? 'border-primary bg-primary/5' : ''
        }`}
        onClick={() => selectConsultation(consultation)}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-muted rounded-full">
                <TypeIcon className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium">{consultation.provider?.businessName || 'Provider'}</p>
                <p className="text-sm text-muted-foreground capitalize">
                  {consultation.consultationType?.toLowerCase()} Consultation
                </p>
              </div>
            </div>
            <Badge className={STATUS_COLORS[consultation.status]}>
              {consultation.status}
            </Badge>
          </div>
          <div className="mt-3 flex items-center space-x-4 text-sm text-muted-foreground">
            <span className="flex items-center">
              <Calendar className="h-3 w-3 mr-1" />
              {consultation.scheduledDate || 'Open Door'}
            </span>
            {consultation.scheduledStart && (
              <span className="flex items-center">
                <Clock className="h-3 w-3 mr-1" />
                {consultation.scheduledStart}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/client/dashboard')} data-testid="consults-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold mt-4">My Consultations</h1>
          <p className="text-muted-foreground">View and manage your consultation bookings</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Consultation List */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="upcoming">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="upcoming">
                  Upcoming
                  {filterConsultations('upcoming').length > 0 && (
                    <span className="ml-2 bg-primary text-primary-foreground text-xs rounded-full px-2">
                      {filterConsultations('upcoming').length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="past">Past</TabsTrigger>
              </TabsList>

              <TabsContent value="upcoming" className="space-y-4 mt-4">
                {filterConsultations('upcoming').length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <Calendar className="h-8 w-8 mx-auto mb-2" />
                      <p>No upcoming consultations</p>
                      <Button className="mt-4" onClick={() => navigate('/')}>
                        Find a Provider
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  filterConsultations('upcoming').map((c) => (
                    <ConsultationCard key={c.id} consultation={c} />
                  ))
                )}
              </TabsContent>

              <TabsContent value="active" className="space-y-4 mt-4">
                {filterConsultations('active').length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <p>No active consultations</p>
                    </CardContent>
                  </Card>
                ) : (
                  filterConsultations('active').map((c) => (
                    <ConsultationCard key={c.id} consultation={c} />
                  ))
                )}
              </TabsContent>

              <TabsContent value="past" className="space-y-4 mt-4">
                {filterConsultations('past').length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <p>No past consultations</p>
                    </CardContent>
                  </Card>
                ) : (
                  filterConsultations('past').map((c) => (
                    <ConsultationCard key={c.id} consultation={c} />
                  ))
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            {selectedConsultation ? (
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle className="text-lg">Consultation Details</CardTitle>
                  <Badge className={STATUS_COLORS[selectedConsultation.status]}>
                    {selectedConsultation.status}
                  </Badge>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Provider Info */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Provider</p>
                    <p className="font-medium">{selectedConsultation.provider?.businessName}</p>
                    <p className="text-sm">{selectedConsultation.provider?.ownerName}</p>
                    {selectedConsultation.provider?.phone && (
                      <p className="text-sm">{selectedConsultation.provider?.phone}</p>
                    )}
                  </div>

                  {/* Schedule */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Schedule</p>
                    <p>{selectedConsultation.scheduledDate || 'Open Door'}</p>
                    {selectedConsultation.scheduledStart && (
                      <p>{selectedConsultation.scheduledStart} - {selectedConsultation.scheduledEnd}</p>
                    )}
                  </div>

                  {/* Type */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Type</p>
                    <p className="capitalize">{selectedConsultation.consultationType?.toLowerCase()}</p>
                  </div>

                  {/* Price */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Price</p>
                    <p>{formatPrice(selectedConsultation.price)}</p>
                  </div>

                  {/* Meeting Instructions */}
                  {selectedConsultation.meetingInstructions && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Meeting Instructions</p>
                      <p className="text-sm bg-muted p-2 rounded whitespace-pre-wrap">
                        {selectedConsultation.meetingInstructions}
                      </p>
                    </div>
                  )}

                  {/* Messages (for TEXT consultations) */}
                  {selectedConsultation.consultationType === 'TEXT' && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">Messages</p>
                      <div className="max-h-48 overflow-y-auto space-y-2 mb-2 border rounded p-2">
                        {messages.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">No messages yet</p>
                        ) : (
                          messages.map((msg) => (
                            <div 
                              key={msg.id}
                              className={`p-2 rounded text-sm ${
                                msg.senderRole === 'CLIENT' 
                                  ? 'bg-primary text-primary-foreground ml-4' 
                                  : 'bg-muted mr-4'
                              }`}
                            >
                              <p>{msg.message}</p>
                              <p className="text-xs opacity-70 mt-1">
                                {new Date(msg.createdAt).toLocaleString()}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                      {!['CANCELLED', 'NO_SHOW', 'COMPLETED'].includes(selectedConsultation.status) && (
                        <div className="flex space-x-2">
                          <Textarea
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type a message..."
                            rows={2}
                            className="flex-1"
                          />
                          <Button onClick={sendMessage} disabled={sendingMessage || !newMessage.trim()}>
                            {sendingMessage ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* View Provider */}
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => navigate(`/provider/${selectedConsultation.provider?.slug || selectedConsultation.providerId}`)}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" /> View Provider
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2" />
                  <p>Select a consultation to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
